import { useMemo, useState } from "react";
import { archiveCategories, archiveEntries } from "@/mocks/archiveEntries";

const PAGE_SIZE = 4;

const ArchiveGrid = () => {
  const [activeCategory, setActiveCategory] = useState("全部");
  const [keyword, setKeyword] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    const key = keyword.trim().toLowerCase();
    return archiveEntries.filter((entry) => {
      const matchCategory = activeCategory === "全部" || entry.category === activeCategory;
      const matchKeyword =
        key.length === 0 ||
        entry.title.toLowerCase().includes(key) ||
        entry.excerpt.toLowerCase().includes(key) ||
        entry.batch.toLowerCase().includes(key) ||
        entry.tags.some((tag) => tag.toLowerCase().includes(key));
      return matchCategory && matchKeyword;
    });
  }, [activeCategory, keyword]);

  const visibleEntries = filtered.slice(0, visibleCount);

  return (
    <section id="archive" className="w-full bg-background-50 py-16 md:py-24">
      <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <span className="reveal inline-flex items-center gap-2 text-xs font-medium tracking-[0.24em] text-primary-600">
              <span className="h-px w-8 bg-primary-400" />
              存档总览
            </span>
            <h2 className="reveal mt-5 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              精选存档批次 · 大图留档流
            </h2>
            <p className="reveal mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              每一组存档都以批次为单位留档，标注更新时间与记录条数。先浏览封面与摘要，再决定是否深入回看。
            </p>
          </div>

          <div className="reveal w-full lg:w-80">
            <label className="mb-2 block text-xs font-medium tracking-wide text-foreground-500">
              关键词检索
            </label>
            <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100 pl-4 pr-2 py-2 focus-within:border-primary-400">
              <i className="ri-search-line text-base text-foreground-500" />
              <input
                value={keyword}
                onChange={(event) => {
                  setKeyword(event.target.value);
                  setVisibleCount(PAGE_SIZE);
                }}
                placeholder="输入批次号、关键词或标签"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
              />
            </div>
          </div>
        </div>

        <div className="reveal mt-8 inline-flex flex-wrap items-center gap-1 rounded-full border border-background-300 bg-background-100 px-1 py-1">
          {archiveCategories.map((category) => {
            const active = activeCategory === category;
            return (
              <button
                key={category}
                type="button"
                onClick={() => {
                  setActiveCategory(category);
                  setVisibleCount(PAGE_SIZE);
                }}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors cursor-pointer ${
                  active
                    ? "bg-primary-500 font-medium text-background-50"
                    : "text-foreground-600 hover:bg-background-200"
                }`}
              >
                {category}
              </button>
            );
          })}
        </div>

        {visibleEntries.length > 0 ? (
          <div className="mt-10 grid grid-cols-1 gap-6 md:mt-12 md:gap-8 lg:grid-cols-2">
            {visibleEntries.map((entry) => (
              <article
                key={entry.id}
                className="reveal group flex flex-col overflow-hidden rounded-lg border border-background-300 bg-background-100 transition-colors hover:border-secondary-400"
              >
                <div className="relative h-60 w-full overflow-hidden md:h-72">
                  <img
                    src={entry.image}
                    alt={`${entry.title} 封面图`}
                    title={`${entry.title} · ${entry.batch}`}
                    className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent" />
                  <span className="absolute left-4 top-4 rounded-full bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50">
                    {entry.category}
                  </span>
                  <span className="absolute right-4 top-4 rounded-full bg-background-50/90 px-3 py-1.5 text-xs font-medium text-foreground-800 backdrop-blur-sm">
                    {entry.date}
                  </span>
                  <span className="absolute bottom-4 left-4 flex items-center gap-2 text-xs tracking-wide text-background-200">
                    <i className="ri-hashtag" />
                    批次 {entry.batch}
                  </span>
                </div>

                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-heading text-xl font-bold leading-snug text-foreground-950">
                    {entry.title}
                  </h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-600">
                    {entry.excerpt}
                  </p>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {entry.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="mt-6 flex items-center justify-between border-t border-background-300 pt-5">
                    <span className="flex items-center gap-2 text-sm text-foreground-500">
                      <i className="ri-stack-line" />
                      留档 {entry.records} 条
                    </span>
                    <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-300 px-4 py-2 text-sm font-medium text-primary-600 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                      查看留档
                      <i className="ri-arrow-right-line" />
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-12 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
              <i className="ri-file-unknow-line" />
            </span>
            <p className="mt-4 font-heading text-lg font-bold text-foreground-900">
              没有匹配的存档批次
            </p>
            <p className="mt-2 text-sm text-foreground-500">
              换个关键词，或点击「全部」查看完整存档总览。
            </p>
            <button
              type="button"
              onClick={() => {
                setActiveCategory("全部");
                setKeyword("");
              }}
              className="mt-6 whitespace-nowrap rounded-md bg-secondary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600 cursor-pointer"
            >
              重置检索条件
            </button>
          </div>
        )}

        {filtered.length > visibleCount && (
          <div className="mt-12 flex justify-center">
            <button
              type="button"
              onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-7 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
            >
              加载更多存档批次
              <i className="ri-arrow-down-line" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ArchiveGrid;